import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Col, Input, Modal, ModalBody, Row } from 'reactstrap';
import { Checkbox, CheckboxOptionType } from 'ALIASED-antd';
import { AlertMessage } from 'vl-common/src/components/AlertMessage/AlertMessage';
import { LoadingButton } from 'vl-common/src/components/LoadingButton';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getMetaCaseQuery } from 'vl-common/src/schemas/getMetaCase';
import { getMetaAppointmentsQuery } from 'vl-common/src/schemas/getMetaAppointments';
import { useValidateCaseRef } from 'vl-common/src/hooks/useValidateCaseRef';
import useIsClientAdmin from 'vl-common/src/hooks/useIsClientAdmin';
import styled from '@emotion/styled';
import { UnstyledButton } from 'vl-common/src/components/UnstyledButton';
import ServiceApi from 'vl-common/src/services/service.api';
import { getAdminSpecCats } from 'vl-common/src/schemas/getAdminSpecCats';
import { Api } from 'vl-common/src/api';
import { PatientType } from 'vl-common/src/lib/context/ManagePatientContext';

const StyledCheckboxGroup = styled(Checkbox.Group)`
  .ant-checkbox-wrapper {
    font-size: 1rem;
  }
`;

type Props = {
  isOpen: boolean;
  patient: PatientType;
  onCaseCreated: (caseId: number) => void;
  onCancel: () => void;
};

type AlertState = { visibility: boolean; msg: React.ReactNode };

const ALERT_HIDDEN: AlertState = { visibility: false, msg: '' };

const isCaseRefMandatory = (value: string | boolean | number | undefined): boolean => {
  if (value === undefined) return false;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value > 0;
  return value === 'true' || value === '1';
};

const CreateCaseModal = ({ isOpen, patient, onCaseCreated, onCancel }: Props) => {
  const { t } = useTranslation('common');
  const isClientAdmin = useIsClientAdmin();

  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [selectedSubSpecialty, setSelectedSubSpecialty] = useState('');
  const [showSubSpecialtyDropdown, setShowSubSpecialtyDropdown] = useState(false);
  const [caseRef, setCaseRef] = useState('');
  const [selectedCohort, setSelectedCohort] = useState<string | null>(null);
  const [selectedPriority, setSelectedPriority] = useState('');
  const [appliedEligibilityChecks, setAppliedEligibilityChecks] = useState<string[]>([]);
  const [alert, setAlert] = useState<AlertState>(ALERT_HIDDEN);
  const [showVirtualOnlyReason, setShowVirtualOnlyReason] = useState(false);
  const [virtualOnlyReason, setVirtualOnlyReason] = useState('');

  const patientGuid = patient?.user_guid;

  const { validateCaseRef, errorText } = useValidateCaseRef(patientGuid);

  const { data: appointmentsMeta } = useQuery(getMetaAppointmentsQuery({ pat_guid: patientGuid }));
  const { data: caseMeta } = useQuery(
    getMetaCaseQuery({ pat_guid: patientGuid, speccat: selectedSpecialty, specarea: selectedSubSpecialty })
  );

  const { data: specCatsResponse } = useQuery({
    queryKey: ['adminSpecCats', ServiceApi.userGuid, patientGuid],
    queryFn: () =>
      getAdminSpecCats({ admin_guid: ServiceApi.userGuid, pat_guid: patientGuid, rows: 999, enabled: 1, slots: 1 }),
    enabled: isOpen && !!patientGuid
  });

  const { data: subSpecialtyAreas } = useQuery({
    queryKey: ['specificAreas', selectedSpecialty],
    queryFn: async () => {
      const res = await Api.admin.getSpecificAreas({ rows: 999, sort: 'area', speccat: selectedSpecialty });
      return res.data.areas as { area_code: string; area_name: string }[];
    },
    enabled: showSubSpecialtyDropdown && !!selectedSpecialty
  });

  const specialties = specCatsResponse?.cats ?? [];
  const specialtyMetaList = appointmentsMeta?.cats ?? [];
  const casePriorities = caseMeta?.clinical_urgencies ?? [];
  const eligibilityPolicies = caseMeta?.eligibility_policies ?? [];
  const caseCohorts = (caseMeta?.cohorts ?? []).filter((cohort) => cohort.cohort_number !== -1);

  const eligibilityCheckboxOptions: CheckboxOptionType[] = eligibilityPolicies.map((policy) => ({
    label: policy.policy_name,
    value: policy.policy_code
  }));

  const effectivePriority = casePriorities.length === 1 ? casePriorities[0].urg_code : selectedPriority;

  const getIsSpecialtyAndSubSpecialtySelected = () => {
    if (showSubSpecialtyDropdown) {
      return !!selectedSpecialty && !!selectedSubSpecialty;
    }
    return !!selectedSpecialty;
  };
  const canChoosePriority = casePriorities.length > 1 && !!patient && getIsSpecialtyAndSubSpecialtySelected();

  const selectedSpecialtyMeta = specialtyMetaList.find(
    (specialtyMeta) => specialtyMeta?.cat_code === selectedSpecialty
  );
  const isCaseRefRequired = isCaseRefMandatory(selectedSpecialtyMeta?.case_id_mandatory);
  const selectedSpecialtyDetails = specialties.find((specialty) => specialty.cat_code === selectedSpecialty);

  const showCaseCohortSelect = caseCohorts.length > 0;
  const showEligibilityCheckboxGroup = !isClientAdmin && eligibilityCheckboxOptions.length > 0;

  const getIsCreateButtonDisabled = () => {
    const isMissingRequiredCaseRef = isCaseRefRequired && !caseRef?.trim();
    const specialtyRequireSubSpecialty = !!selectedSpecialtyDetails?.no_areas;
    const missingRequiredSubSpecialty = specialtyRequireSubSpecialty && !selectedSubSpecialty;

    return isMissingRequiredCaseRef || !selectedSpecialty || missingRequiredSubSpecialty || isCreatingCase;
  };

  const showAlert = (msg: React.ReactNode, autoHide = false) => {
    setAlert({ visibility: true, msg });
    if (autoHide) {
      const timer = setTimeout(() => {
        setAlert(ALERT_HIDDEN);
        clearTimeout(timer);
      }, 7000);
    }
  };

  const resetFields = () => {
    setSelectedSpecialty('');
    setSelectedSubSpecialty('');
    setShowSubSpecialtyDropdown(false);
    setCaseRef('');
    setSelectedCohort(null);
    setSelectedPriority('');
    setAppliedEligibilityChecks([]);
    setAlert(ALERT_HIDDEN);
    setVirtualOnlyReason('');
    setShowVirtualOnlyReason(false);
  };

  const handleClose = () => {
    resetFields();
    onCancel();
  };

  const handleSpecialtyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const catCode = e.target.value;
    const catDetails = specialties.find((cat) => cat.cat_code === catCode);
    const needsSubSpecialty = !!catDetails?.no_areas && !catDetails?.standard_subspecialty;
    const standardSubspecialty = catDetails?.standard_subspecialty ?? '';

    setAppliedEligibilityChecks([]);
    setSelectedCohort(null);
    setSelectedSpecialty(catCode);
    setShowSubSpecialtyDropdown(needsSubSpecialty);
    setSelectedSubSpecialty(needsSubSpecialty ? '' : standardSubspecialty);

    if (!catCode) setSelectedPriority('');
  };

  const handleSubSpecialtyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedSubSpecialty(e.target.value);
  };

  const handlePriorityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedPriority(e.target.value);
  };

  const handleCohortChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedCohort(e.target.value);
  };

  const handleCaseRefChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCaseRef(e.target.value);
  };

  const handleCreateError = (e: { response?: { status?: number; data?: { message?: string } } }) => {
    switch (e.response?.status) {
      case 409:
        showAlert(
          <p style={{ padding: 0, margin: 0, textAlign: 'center' }}>
            {t('An')}{' '}
            <em style={{ fontWeight: 'bold' }}>
              {selectedSpecialty}, {selectedSubSpecialty}
            </em>{' '}
            {t('case already exists for ')}
            {patient?.title} {patient?.first_name} {patient?.last_name}
          </p>
        );
        break;
      case 417:
        showAlert(
          <p style={{ padding: 0, margin: 0, textAlign: 'center' }}>
            {t('A case with reference "')}
            {caseRef}
            {t('" already exists.')}
          </p>
        );
        break;
      case 406:
        showAlert(
          <p style={{ padding: 0, margin: 0, textAlign: 'center' }}>
            {e.response?.data?.message ?? t('Case cannot be created as the patient is uninsured')}
          </p>
        );
        break;
      case 451:
        showAlert(
          <p style={{ padding: 0, margin: 0, textAlign: 'center' }}>
            {t('Case cannot be created as the patient is uninsured')}
          </p>
        );
        break;
      default:
        showAlert(
          <p style={{ padding: 0, margin: 0, textAlign: 'center' }}>
            {t('Failed to create case. Please check your connection and')}{' '}
            <UnstyledButton
              data-testid="refresh-button"
              style={{ textDecoration: 'underline', cursor: 'pointer' }}
              onClick={() => window.location.reload()}
            >
              {t('refresh to try again')}
            </UnstyledButton>
          </p>
        );
    }
  };

  const { mutate: createCase, isPending: isCreatingCase } = useMutation({
    mutationFn: async () => {
      const res = await Api.cases.putCreateCase({
        pat_guid: patientGuid,
        speccat: selectedSpecialty,
        specarea: selectedSubSpecialty,
        client_case_id: caseRef,
        urg_code: effectivePriority,
        case_only: 1,
        ...(selectedCohort && { cohort_number: Number(selectedCohort) })
      });
      const caseId = res.data.case_id as number;
      const postCaseEligibilityPolicy = async (policyCode: string, virtualOnlyReason?: string) =>
        Api.referrers.postEligibilityPolicy(caseId, policyCode, true, virtualOnlyReason);
      const arrayOfEligibilityChecks = Array.isArray(appliedEligibilityChecks)
        ? appliedEligibilityChecks
        : [appliedEligibilityChecks];
      await Promise.all(
        (arrayOfEligibilityChecks ?? [])
          .filter(Boolean)
          .map((policyCode) =>
            postCaseEligibilityPolicy(policyCode, policyCode === 'VIRTUAL_ONLY' ? virtualOnlyReason : undefined)
          )
      );
      return caseId;
    },
    onSuccess: (caseId) => {
      onCaseCreated(caseId);
      resetFields();
    },
    onError: handleCreateError
  });

  const handleCreateCase = () => {
    setAlert(ALERT_HIDDEN);
    if (!validateCaseRef(caseRef)) {
      showAlert(<p style={{ padding: 0, margin: 0, textAlign: 'center' }}>{errorText}</p>);
      return;
    }
    createCase();
  };

  return (
    <Modal size="lg" isOpen={isOpen} toggle={handleClose} centered backdrop="static" keyboard={false}>
      <ModalBody>
        <Row style={styles.container}>
          <Col className="vl-flex-col-center">
            <div style={styles.title}>
              {t('Create')} <span style={styles.titleDarkSide}>{t('Case')}</span>
            </div>

            <AlertMessage message={alert.msg} visibility={alert.visibility} />

            <div style={styles.subText}>
              {t('Please enter a Case Reference for this case')}
              {patient && isCaseRefRequired && <span className="vl-product-satsuma">*</span>}
            </div>

            <Row style={styles.inputContainer} className="vl-flex-row-center">
              <Col xs="8" className="vl-filter-box">
                <Input
                  type="text"
                  placeholder={t('Enter Case Reference')}
                  value={caseRef}
                  data-testid="case-ref-input"
                  onChange={handleCaseRefChange}
                />
              </Col>
            </Row>

            <div style={styles.subText}>{t('Please select from a list of specialties we service')}</div>

            <Row style={styles.inputContainer} className="vl-flex-row-center">
              <Col xs="8" className="vl-filter-box">
                <Input
                  className="minimal"
                  type="select"
                  data-testid="speciality-select"
                  value={selectedSpecialty}
                  onChange={handleSpecialtyChange}
                >
                  <option value="">{t('Select specialty from dropdown')}</option>
                  {specialties.map((item) => (
                    <option key={item.cat_code} value={item.cat_code}>
                      {item.cat_name}
                    </option>
                  ))}
                </Input>

                {showSubSpecialtyDropdown && !!subSpecialtyAreas?.length && (
                  <Input
                    style={styles.subCatDropdown}
                    className="minimal"
                    type="select"
                    data-testid="sub-speciality-select"
                    value={selectedSubSpecialty}
                    onChange={handleSubSpecialtyChange}
                  >
                    <option value="">{t('Select from dropdown')}</option>
                    {subSpecialtyAreas.map((item) => (
                      <option key={item.area_code} value={item.area_code}>
                        {item.area_name}
                      </option>
                    ))}
                  </Input>
                )}
              </Col>
            </Row>

            <div style={styles.urgText}>{t('Please choose priority')}</div>

            <Row style={styles.inputContainer} className="vl-flex-row-center">
              <Col xs="8" className="vl-filter-box">
                <Input
                  className="minimal"
                  type="select"
                  value={effectivePriority}
                  data-testid="select-priority"
                  onChange={handlePriorityChange}
                  disabled={!canChoosePriority}
                >
                  <option value="">{t('Select from dropdown')}</option>
                  {casePriorities.map((item) => (
                    <option key={item.urg_code} value={item.urg_code}>
                      {item.urg_desc}
                    </option>
                  ))}
                </Input>
              </Col>
            </Row>

            {showCaseCohortSelect && (
              <>
                <div style={styles.subText}>{t('Select cohort')}</div>
                <Row style={styles.inputContainer} className="vl-flex-row-center">
                  <Col xs="8" className="vl-filter-box">
                    <Input
                      className="minimal"
                      type="select"
                      value={selectedCohort ?? ''}
                      data-testid="select-cohort"
                      onChange={handleCohortChange}
                      disabled={!canChoosePriority}
                    >
                      <option value="">{t('Select from dropdown')}</option>
                      {caseCohorts.map((cohort) => (
                        <option key={cohort.cohort_number} value={cohort.cohort_number}>
                          {cohort.cohort_description}
                        </option>
                      ))}
                    </Input>
                  </Col>
                </Row>
              </>
            )}

            {showEligibilityCheckboxGroup && (
              <>
                <div style={styles.urgText}>{t('Please select eligibility checks')}</div>
                <Row style={styles.inputContainer} className="vl-flex-row-center">
                  <Col xs="8" className="vl-filter-box">
                    <StyledCheckboxGroup
                      options={eligibilityCheckboxOptions}
                      value={appliedEligibilityChecks}
                      onChange={(checkedValues) => {
                        const isVirtualOnlyChecked = checkedValues.includes('VIRTUAL_ONLY');
                        setShowVirtualOnlyReason(isVirtualOnlyChecked);
                        setAppliedEligibilityChecks(checkedValues as string[]);
                      }}
                    />
                  </Col>
                </Row>
              </>
            )}

            {showVirtualOnlyReason && (
              <>
                <div style={styles.urgText}>{t('Please choose virtual only reason')}</div>
                <Row style={styles.inputContainer} className="vl-flex-row-center">
                  <Col xs="8" className="vl-filter-box">
                    <Input
                      className="minimal"
                      type="select"
                      value={virtualOnlyReason}
                      onChange={(e) => {
                        setVirtualOnlyReason(e.target.value);
                      }}
                    >
                      <option value="">{t('Select from dropdown')}</option>
                      {caseMeta?.virtual_only_reason_codes.map((reason) => {
                        return (
                          <option key={reason.virtual_only_reason_code} value={reason.virtual_only_reason_code}>
                            {reason.virtual_only_reason_desc}
                          </option>
                        );
                      })}
                    </Input>
                  </Col>
                </Row>
              </>
            )}

            <div className="vl-flex-row-center" style={styles.btnsContainer}>
              <Button style={styles.btnGray} type="button" onClick={handleClose} data-testid="cancel-create-case">
                {t('Cancel')}
              </Button>
              <LoadingButton
                style={styles.btnMain}
                type="button"
                loading={isCreatingCase}
                data-testid="create-case-button"
                disabled={getIsCreateButtonDisabled()}
                onClick={handleCreateCase}
              >
                {t('Create Case')}
              </LoadingButton>
            </div>
          </Col>
        </Row>
      </ModalBody>
    </Modal>
  );
};

export default CreateCaseModal;

const styles: Record<string, React.CSSProperties> = {
  container: { padding: '30px' },
  btnMain: {
    width: '150px',
    backgroundColor: 'var(--productPrimary)',
    fontSize: '14px',
    padding: '4px 10px',
    borderColor: 'var(--productPrimary)',
    color: 'var(--productPrimaryDark)'
  },
  subCatDropdown: {
    marginTop: '10px'
  },
  btnGray: {
    width: '110px',
    backgroundColor: 'var(--productGray)',
    fontSize: '14px',
    padding: '4px 10px',
    borderColor: 'var(--productGray)',
    marginRight: '10px'
  },
  subText: {
    color: 'var(--productGray)',
    fontSize: '14px',
    marginTop: '20px',
    marginBottom: '10px'
  },
  urgText: {
    color: 'var(--productGray)',
    fontSize: '14px',
    marginTop: '20px',
    marginBottom: '10px'
  },
  btnsContainer: { marginBottom: '30px', marginTop: '30px', width: '100%' },
  inputContainer: { position: 'relative', width: '100%' },
  title: { color: 'var(--productPink)', fontSize: '30px', fontWeight: 'bold' },
  titleDarkSide: { color: 'var(--productGray)', fontWeight: 'bold' }
};
